import type { FC } from 'react';
import { useFormik } from 'formik';
import { Input, Button } from '@selfinspectionorg/ui-kit';
import { useNavigate } from '@tanstack/react-router';
import { MfaSetup, MfaVerify } from '../mfa';
import { UpdatePasswordForm } from '../update-password';
import styles from './LoginForm.module.scss';
import { useLoginWizard } from './useLoginWizard';
import { validationSchema } from './form/validation.schema';
import { type LoginFormValues } from './form/formValue.type';
import { SelectAccountStep } from './select-account';
import { PasswordInput } from '@/components/form/PasswordInput';
import { Card, FullscreenLoader } from '@/components/ui';
import { LOCATION_REVOKED_MESSAGE } from '@/shared/utils';

interface Props {
  isLocationRevoked: boolean;
}

// Any `returnUrl` stays in the `/login` URL query across every inline step (the wizard never
// navigates) and is read from there at the post-auth redirect — no prop threading needed.
export const LoginForm: FC<Props> = ({ isLocationRevoked }) => {
  const {
    step,
    mfaChallenge,
    accounts,
    error,
    changeToken,
    isPending,
    submitCredentials,
    onVerified,
    onEnrollmentComplete,
    onEnrollmentExit,
    onPasswordChanged,
    onPasswordChangeExpired,
    onPasswordChangeBack,
    selectAccount,
    resetToCredentials,
  } = useLoginWizard();

  const form = useFormik<LoginFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      if (isPending) {
        return;
      }

      await submitCredentials(values.email, values.password);
    },
  });

  const navigate = useNavigate();

  const handleForgotPasswordClick = () => {
    navigate({ to: '/forgot-password' });
  };

  // Terminal step — the session is adopted and the full-page redirect is underway; hold a loader
  // instead of letting the credential form flash while the browser swaps documents.
  if (step === 'done') {
    return <FullscreenLoader />;
  }

  // Step 2 — inline MFA verification on the same `/login` URL. Back or an expired session drops the
  // user to the fresh credential form (`resetToCredentials`); a completed ceremony hands its
  // dual-outcome result to `onVerified`, which captures the proof and advances to the pick step.
  if (step === 'mfa' && mfaChallenge) {
    return (
      <MfaVerify
        mfaPending={mfaChallenge.mfaPending}
        mfaMethods={mfaChallenge.mfaMethods}
        onSuccess={onVerified}
        onBack={() => resetToCredentials()}
        onExpired={() => resetToCredentials()}
      />
    );
  }

  // Step 2 (enrollment variant) — inline MFA enrollment on the same `/login` URL. Completion hands
  // the raw confirm result up so the wizard captures the `accountsProof` and continues to account
  // selection without re-entering credentials; any exit resets to the fresh credential form.
  if (step === 'mfa-setup' && mfaChallenge) {
    return (
      <MfaSetup
        mfaPending={mfaChallenge.mfaPending}
        mfaMethods={mfaChallenge.mfaMethods}
        onComplete={onEnrollmentComplete}
        onExit={onEnrollmentExit}
      />
    );
  }

  // Forced password change — inline on the same `/login` URL, the `changeToken` in wizard memory
  // only. Success silently re-logs-in with the new password and the remembered account selection.
  if (step === 'update-password' && changeToken) {
    return (
      <UpdatePasswordForm
        changeToken={changeToken}
        onSuccess={onPasswordChanged}
        onExpired={onPasswordChangeExpired}
        onBack={onPasswordChangeBack}
      />
    );
  }

  // Step 3 — in-page account picker. Back returns to the credential form (a fresh sign-in).
  if (step === 'pick') {
    return (
      <SelectAccountStep
        accounts={accounts}
        onConfirm={selectAccount}
        onBack={() => resetToCredentials()}
        isConfirming={isPending}
        error={error}
      />
    );
  }

  // Step 1 — credentials. `useLoginWizard` catches every failure of `check-credentials` and `login`,
  // so `error` is the single source; the mutations' own errors are not read.
  const fieldError = form.errors.password || error || undefined;

  return (
    <Card className={styles.wrapper}>
      <Card.Title title="Welcome back" />
      {isLocationRevoked && <p className={styles.notice}>{LOCATION_REVOKED_MESSAGE}</p>}
      <form noValidate onSubmit={form.handleSubmit}>
        <Input
          label="Email"
          required
          name="email"
          type="email"
          placeholder="example@gmail.com"
          value={form.values.email}
          onChange={form.handleChange}
          errored={!!form.errors.email}
          helperMessage={form.errors.email}
          testId="email-login"
        />
        <PasswordInput
          label="Password"
          required
          name="password"
          placeholder="Enter your password"
          value={form.values.password}
          onChange={form.handleChange}
          errored={!!fieldError}
          helperMessage={fieldError}
          testId="password-login"
        />
        <div className={styles.buttonsContainer}>
          <Button.Ghost data-testid="forgot-password-button" onClick={handleForgotPasswordClick}>
            Forgot password?
          </Button.Ghost>
          <Button
            data-testid="login-button"
            type="submit"
            width="100%"
            disabled={isPending || form.isSubmitting}
            isLoading={isPending || form.isSubmitting}
          >
            Login
          </Button>
        </div>
      </form>
    </Card>
  );
};
